import React, { useRef, useEffect, useCallback } from 'react';

import { useGroupContext } from './context';
import { SeparatorStyled } from './style';
import type { SeparatorProps } from './types';
import { useId } from './use-id';

export const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  ({ children, className, elementRef, id: idProp, style, ...rest }, ref) => {
    const generatedId = useId();
    const separatorId = idProp ?? generatedId;
    const internalRef = useRef<HTMLDivElement>(null);
    const {
      registerSeparator,
      unregisterSeparator,
      startDrag,
      orientation,
      disabled,
      disableCursor,
    } = useGroupContext();

    // Find adjacent panels and register separator
    // Use requestAnimationFrame to ensure panels are registered before separator
    useEffect(() => {
      if (!internalRef.current) return;

      const register = () => {
        if (!internalRef.current) return;

        const groupElement = internalRef.current.closest('[data-group]');
        if (!groupElement) return;

        const children = Array.from(groupElement.children);
        const separatorIndex = children.indexOf(internalRef.current);
        if (separatorIndex === -1) return;

        // Find the panel before this separator
        let panel1: HTMLElement | null = null;
        for (let i = separatorIndex - 1; i >= 0; i--) {
          const child = children[i] as HTMLElement;
          if (child.hasAttribute('data-panel')) {
            panel1 = child;
            break;
          }
        }

        // Find the panel after this separator
        let panel2: HTMLElement | null = null;
        for (let i = separatorIndex + 1; i < children.length; i++) {
          const child = children[i] as HTMLElement;
          if (child.hasAttribute('data-panel')) {
            panel2 = child;
            break;
          }
        }

        if (panel1 && panel2) {
          const panel1Id = panel1.id || panel1.getAttribute('data-testid') || '';
          const panel2Id = panel2.id || panel2.getAttribute('data-testid') || '';
          if (panel1Id && panel2Id) {
            registerSeparator(String(separatorId), panel1Id, panel2Id);
          }
        }
      };

      // Delay registration to ensure panels are mounted
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(register);
      });

      return () => {
        cancelAnimationFrame(rafId);
        unregisterSeparator(String(separatorId));
      };
    }, [separatorId, registerSeparator, unregisterSeparator]);

    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (disabled) {
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        startDrag(String(separatorId), e.clientX, e.clientY);
      },
      [disabled, separatorId, startDrag],
    );

    // Sync elementRef and internalRef
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(internalRef.current);
        } else if (ref && typeof ref === 'object' && 'current' in ref) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ref as any).current = internalRef.current;
        }
      }
      if (elementRef) {
        if (typeof elementRef === 'function') {
          elementRef(internalRef.current);
        } else if (elementRef && typeof elementRef === 'object' && 'current' in elementRef) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (elementRef as any).current = internalRef.current;
        }
      }
    }, [ref, elementRef]);

    return (
      <SeparatorStyled
        ref={internalRef}
        id={String(separatorId)}
        data-separator
        data-testid={String(separatorId)}
        role="separator"
        aria-orientation={orientation}
        aria-valuemin={0}
        aria-valuemax={100}
        orientation={orientation}
        disabled={disabled || disableCursor}
        className={className}
        onMouseDown={handleMouseDown}
        style={style}
        {...rest}
      >
        {children}
      </SeparatorStyled>
    );
  },
);

Separator.displayName = 'Separator';
